import Image, { type ImageOptions } from '@tiptap/extension-image';
import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

import { getApiClient } from '../../api/client';
import { fetchAuthenticatedFileBlobUrl, isStoredFileContentUrl } from '../../api/system/files';

/** 리사이즈 최소 폭(px). */
const MIN_WIDTH = 48;

/**
 * 저장 포맷이 마크다운(tiptap-markdown)이라 `<img width>` 속성은 왕복에서 유실된다.
 * 그래서 폭을 Obsidian 방식으로 alt 뒤에 `|숫자`로 인코딩(`![alt|300](src)`)해
 * 표준 마크다운으로 안전하게 왕복시킨다. 폭이 없으면 기존 `![alt](src)` 그대로라 하위호환.
 */
function encodeAlt(alt: string, width: number | null): string {
  const base = alt ?? '';
  return width && width > 0 ? `${base}|${Math.round(width)}` : base;
}

function parseWidthFromAlt(raw: string | null): number | null {
  const match = /\|(\d+)$/.exec(raw ?? '');
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function stripWidthFromAlt(raw: string | null): string {
  return (raw ?? '').replace(/\|\d+$/, '');
}

function readWidthAttr(element: HTMLElement): number | null {
  const explicit = element.getAttribute('width') || element.style?.width || '';
  const parsed = parseInt(String(explicit), 10);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return parseWidthFromAlt(element.getAttribute('alt'));
}

/**
 * SPA JWT 환경에서 `<img src="/api/files/...">` 는 Authorization 헤더 없이 요청되어
 * 401이 난다. NodeView에서 axios로 blob 을 받아 표시하고, 문서 attrs.src 는 API
 * 경로(마크다운 저장값)를 유지한다. 편집 가능 상태에서는 우하단 핸들로 폭을 조절한다.
 */
export interface AuthenticatedImageOptions extends ImageOptions {
  /** 이미지 클릭 시 전체 크기 미리보기(라이트박스) 콜백. 미지정 시 비활성. */
  onPreview: ((url: string) => void) | null;
}

export const AuthenticatedImage = Image.extend<AuthenticatedImageOptions>({
  addOptions() {
    return {
      ...(this.parent?.() as ImageOptions),
      onPreview: null,
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      alt: {
        default: null,
        parseHTML: (element) => stripWidthFromAlt(element.getAttribute('alt')) || null,
        renderHTML: (attributes) => (attributes.alt ? { alt: attributes.alt as string } : {}),
      },
      width: {
        default: null,
        parseHTML: (element) => readWidthAttr(element),
        renderHTML: (attributes) => {
          const width = typeof attributes.width === 'number' ? attributes.width : null;
          return width ? { width, style: `width: ${width}px` } : {};
        },
      },
    };
  },

  addStorage() {
    return {
      markdown: {
        serialize(state: MarkdownSerializerState, node: ProseMirrorNode) {
          const width = typeof node.attrs.width === 'number' ? node.attrs.width : null;
          const altText = typeof node.attrs.alt === 'string' ? node.attrs.alt : '';
          const alt = state.esc(encodeAlt(altText, width));
          const src = String(node.attrs.src ?? '').replace(/[()]/g, '\\$&');
          const title = node.attrs.title
            ? ` "${String(node.attrs.title).replace(/"/g, '\\"')}"`
            : '';
          state.write(`![${alt}](${src}${title})`);
          // 블록 노드는 반드시 블록을 닫아야 다음 블록(체크리스트 등)이 새 줄에서
          // 시작한다. 누락 시 `![...](...)- [x] ...` 로 붙어 재파싱이 깨진다.
          state.closeBlock(node);
        },
        parse: {
          // markdown → HTML(markdown-it) → addAttributes.parseHTML 경로로 폭 복원
        },
      },
    };
  },

  addNodeView() {
    const onPreview = this.options.onPreview ?? null;
    return ({ node, editor, getPos }) => buildImageNodeView(node, editor, getPos, onPreview);
  },
});

/** prosemirror-markdown serializer state 최소 형태. */
interface MarkdownSerializerState {
  esc(value: string): string;
  write(value: string): void;
  closeBlock(node: ProseMirrorNode): void;
}

/** 이미지 마크다운으로 시작하고, 뒤에 블록 마커(목록/체크/제목/인용/이미지)가 붙은 줄. */
const IMAGE_BLOCK_CONCAT_RE = /^(!\[[^\]\n]*\]\([^)\n]*\))\s*((?:- |\d+\. |#{1,6} |> |!\[).*)$/;

/**
 * 과거 serialize 가 블록을 닫지 않아(`closeBlock` 누락) 이미지와 다음 블록이
 * 한 줄로 붙어 저장된 마크다운(`![...](...)- [x] ...`)을 블록 단위로 분리한다.
 * 정상 문서에는 나타날 수 없는 형태만 매칭하므로 멱등·무해하다.
 */
export function healImageBlockConcatMarkdown(markdown: string): string {
  if (!markdown || !markdown.includes('![')) return markdown;
  const out: string[] = [];
  for (let line of markdown.split('\n')) {
    let match = IMAGE_BLOCK_CONCAT_RE.exec(line);
    while (match?.[1] && match[2] !== undefined) {
      out.push(match[1], '');
      line = match[2];
      match = IMAGE_BLOCK_CONCAT_RE.exec(line);
    }
    out.push(line);
  }
  return out.join('\n');
}

type GetPos = () => number | undefined;

function buildImageNodeView(
  initialNode: ProseMirrorNode,
  editor: Editor,
  getPos: GetPos,
  onPreview: ((url: string) => void) | null,
) {
  let node = initialNode;

  const dom = document.createElement('div');
  dom.className = 'ax-rich-text-editor__image-wrap';

  const img = document.createElement('img');
  img.className = 'ax-rich-text-editor__image';
  img.draggable = false;

  // 클릭 시 원본 크기 미리보기(라이트박스). 표시 중인 URL(blob 또는 원본 src)을 넘긴다.
  const onImgClick = (event: MouseEvent): void => {
    if (!onPreview) return;
    const url = blobUrl ?? (typeof node.attrs.src === 'string' ? node.attrs.src : '');
    if (!url) return;
    event.preventDefault();
    onPreview(url);
  };
  if (onPreview) {
    img.style.cursor = 'zoom-in';
    img.addEventListener('click', onImgClick);
  }

  const applyAlt = (): void => {
    const alt = typeof node.attrs.alt === 'string' ? node.attrs.alt : '';
    if (alt) img.alt = alt;
    else img.removeAttribute('alt');
    if (typeof node.attrs.title === 'string' && node.attrs.title) img.title = node.attrs.title;
    else img.removeAttribute('title');
  };

  const applyWidth = (width: number | null): void => {
    if (width && width > 0) {
      dom.style.width = `${width}px`;
      img.style.width = '100%';
    } else {
      dom.style.removeProperty('width');
      img.style.removeProperty('width');
    }
  };

  applyAlt();
  applyWidth(typeof node.attrs.width === 'number' ? node.attrs.width : null);

  let blobUrl: string | null = null;
  const loadSrc = (): void => {
    const src = typeof node.attrs.src === 'string' ? node.attrs.src : '';
    if (src && isStoredFileContentUrl(src)) {
      void fetchAuthenticatedFileBlobUrl(getApiClient(), src)
        .then((url) => {
          blobUrl = url;
          img.src = url;
        })
        .catch(() => {
          img.alt = img.alt || '이미지를 불러올 수 없습니다';
          dom.classList.add('ax-rich-text-editor__image-wrap--error');
        });
    } else if (src) {
      img.src = src;
    }
  };
  loadSrc();

  dom.appendChild(img);

  const commitWidth = (width: number | null): void => {
    const pos = getPos();
    if (typeof pos !== 'number') return;
    const { view } = editor;
    const target = view.state.doc.nodeAt(pos);
    if (!target) return;
    view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...target.attrs, width }));
  };

  // 편집 가능 상태에서만 네 모서리 리사이즈 핸들 노출. readonly는 CSS(contenteditable="false")로도 숨김.
  // dir: 왼쪽 모서리는 왼쪽으로 끌 때(-1), 오른쪽 모서리는 오른쪽으로 끌 때(+1) 폭이 커진다.
  const corners: { name: string; dir: number }[] = [
    { name: 'nw', dir: -1 },
    { name: 'ne', dir: 1 },
    { name: 'sw', dir: -1 },
    { name: 'se', dir: 1 },
  ];
  const handles: { el: HTMLElement; onDown: (event: PointerEvent) => void }[] = [];

  for (const corner of corners) {
    const handle = document.createElement('span');
    handle.className = `ax-rich-text-editor__image-resize ax-rich-text-editor__image-resize--${corner.name}`;
    handle.setAttribute('aria-hidden', 'true');
    handle.contentEditable = 'false';

    const onDown = (event: PointerEvent): void => {
      if (!editor.isEditable) return;
      event.preventDefault();
      event.stopPropagation();
      const startX = event.clientX;
      const startWidth = img.getBoundingClientRect().width;
      const maxWidth = dom.parentElement?.getBoundingClientRect().width ?? Number.POSITIVE_INFINITY;
      let pending = startWidth;

      const onMove = (moveEvent: PointerEvent): void => {
        const next = Math.round(startWidth + corner.dir * (moveEvent.clientX - startX));
        pending = Math.max(MIN_WIDTH, Math.min(next, maxWidth));
        applyWidth(pending);
      };
      const onUp = (): void => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        commitWidth(Math.round(pending));
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    };

    handle.addEventListener('pointerdown', onDown);
    dom.appendChild(handle);
    handles.push({ el: handle, onDown });
  }

  return {
    dom,
    update(updatedNode: ProseMirrorNode) {
      if (updatedNode.type.name !== node.type.name) return false;
      if (updatedNode.attrs.src !== node.attrs.src) return false;
      node = updatedNode;
      applyAlt();
      applyWidth(typeof node.attrs.width === 'number' ? node.attrs.width : null);
      return true;
    },
    destroy() {
      img.removeEventListener('click', onImgClick);
      for (const { el, onDown } of handles) el.removeEventListener('pointerdown', onDown);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    },
  };
}
